import React, { useState, useEffect, useContext } from 'react';
import './AdminLogin.css';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebaseConfig';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, setDoc, doc, query, where } from 'firebase/firestore';
import { UserContext } from '../userContext.js';

function AdminLogin() {
  const navigate = useNavigate();
  const { setUser } = useContext(UserContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        checkUserAccess(user.email, user.uid);
      }
      setIsInitialized(true);
    });
    return unsubscribe;
  }, []);

  const checkUserAccess = async (email, uid) => {
    try {
      setLoading(true);
      
      // 1. Verificar na coleção 'admins' (coleção antiga)
      const adminsRef = collection(db, 'admins');
      const adminsSnapshot = await getDocs(adminsRef);
      const admins = adminsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const adminAntigo = admins.find(admin => admin.email === email && admin.active !== false);

      if (adminAntigo) {
        // É um admin da coleção antiga
        const userData = {
          uid: uid,
          email: email,
          name: adminAntigo.nome || adminAntigo.name || 'Administrador',
          role: 'admin',
          active: true
        };
        setUser(userData);
        navigate('/admin-panel');
        return;
      }

      // 2. Verificar na coleção 'usuarios' (nova coleção) - admin e especialista
      const usuariosRef = collection(db, 'usuarios');
      const usuariosQuery = query(usuariosRef, where('email', '==', email));
      const usuariosSnapshot = await getDocs(usuariosQuery);
      
      if (!usuariosSnapshot.empty) {
        const userData = usuariosSnapshot.docs[0].data();
        
        // Verificar se é admin ou especialista e se está ativo
        if ((userData.role === 'admin' || userData.role === 'especialista') && userData.active !== false) {
          const userInfo = {
            uid: uid,
            email: email,
            name: userData.name || 'Usuário',
            role: userData.role,
            telefone: userData.telefone || '',
            bio: userData.bio || '',
            active: userData.active !== false
          };
          setUser(userInfo);
          navigate('/admin-panel');
          return;
        }
      }

      // 3. Se não encontrou ou não tem permissão
      setError('Apenas administradores e especialistas autorizados podem acessar este painel.');
      auth.signOut();
      
    } catch (error) {
      console.error('Erro ao verificar acesso:', error);
      setError('Erro ao verificar permissões de acesso.');
      auth.signOut();
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // O checkUserAccess será chamado automaticamente pelo onAuthStateChanged
    } catch (error) {
      setLoading(false);
      let mensagemErro = 'Erro ao fazer login: ';
      
      switch (error.code) {
        case 'auth/user-not-found':
          mensagemErro += 'E-mail não encontrado.';
          break;
        case 'auth/wrong-password':
          mensagemErro += 'Senha incorreta.';
          break;
        case 'auth/invalid-email':
          mensagemErro += 'E-mail inválido.';
          break;
        case 'auth/too-many-requests':
          mensagemErro += 'Muitas tentativas de login. Tente novamente mais tarde.';
          break;
        default:
          mensagemErro += error.message;
      }
      
      setError(mensagemErro);
    }
  };

  // Inicializar admin padrão se não existir
  useEffect(() => {
    if (!isInitialized) {
      const initializeAdmins = async () => {
        try {
          const adminsRef = collection(db, 'admins');
          const snapshot = await getDocs(adminsRef);
          if (snapshot.empty) {
            await setDoc(doc(db, 'admins', 'admin1'), {
              email: 'conecta.ead.app@gmail.com',
              nome: 'Administrador Principal',
              active: true,
              createdAt: new Date()
            });
          }
        } catch (error) {
          console.error('Erro ao inicializar admins:', error);
        }
      };
      initializeAdmins();
    }
  }, [isInitialized]);

  return (
    <div className="admin-login-page">
      <div className="admin-login-container">
        <h2 className="admin-login-title">Painel Administrativo - Login</h2>
        <p style={{ 
          textAlign: 'center', 
          color: '#6c757d', 
          marginBottom: '2rem',
          fontSize: '0.9rem'
        }}>
          Acesso para <strong>Administradores</strong> e <strong>Especialistas</strong>
        </p>
        
        <form onSubmit={handleLogin}>
          <div className="admin-login-field">
            <label htmlFor="admin-email">E-mail</label>
            <input
              type="email"
              id="admin-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              placeholder="Digite seu e-mail"
            />
          </div>
          <div className="admin-login-field">
            <label htmlFor="admin-password">Senha</label>
            <input
              type="password"
              id="admin-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              placeholder="Digite sua senha"
            />
          </div>
          {error && <p className="admin-login-error">{error}</p>}
          <button 
            type="submit" 
            className="admin-login-button"
            disabled={loading}
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
          <a href="#" className="admin-forgot-password">Esqueceu sua senha?</a>
        </form>

        {/* Informações adicionais */}
        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          background: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          fontSize: '0.85rem',
          color: '#6c757d'
        }}>
          <h4 style={{ margin: '0 0 0.5rem 0', color: '#495057' }}>ℹ️ Tipos de Acesso:</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <strong style={{ color: '#e74c3c' }}>👨‍💼 Administrador:</strong>
              <br />Acesso completo ao sistema
            </div>
            <div>
              <strong style={{ color: '#3498db' }}>👨‍🎓 Especialista:</strong>
              <br />Gerenciar materiais e responder perguntas
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminLogin;